import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, type Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { MoveCompanyContentModal } from "@/components/Profiles/MoveCompanyContentModal";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { companies } from "@/api/addressBook.service";
import {
  lawyerProfilesService,
  firmProfilesService,
  firmTabsService,
  articlesService,
  teamPagesService,
} from "@/api/profiles";
import type { ContentStatus, FirmTabType, ListResponse } from "@/api/profiles";
import { ROUTES } from "@/routes";
import styles from "./FirmDetail.module.css";
import { FirmLogosSection } from "./FirmLogosSection";

type ContentType = "lawyer-profiles" | "firm-profiles" | "firm-tabs" | "articles" | "team-pages";

type ContentRow = {
  id: string;
  title: string;
  status: ContentStatus;
  createdAt: string;
  /** Firm-tab kind, only present for the "firm-tabs" section. Used to build the per-kind edit link. */
  tabType?: FirmTabType;
};

type SectionData = {
  type: ContentType;
  label: string;
  rows: ContentRow[];
  loading: boolean;
  error: string | null;
};

type BaseContentItem = {
  id: string;
  status: ContentStatus;
  createdAt: string;
  /** Present on firm-tab rows (tagged by firmTabsService.list); used for per-kind edit links. */
  tabType?: FirmTabType;
};

type FirmListFn = (filters: {
  companyId?: string;
  limit?: number;
  offset?: number;
}) => Promise<ListResponse<BaseContentItem>>;

const SECTIONS: Array<{
  type: ContentType;
  label: string;
  listFn: FirmListFn;
  /** Base path of the cross-firm list page for this resource. */
  listPath: string;
}> = [
  {
    type: "lawyer-profiles",
    label: "Lawyer Profiles",
    listFn: filters => lawyerProfilesService.list(filters),
    listPath: ROUTES.profiles.lawyerProfiles,
  },
  {
    type: "firm-profiles",
    label: "Firm Profiles",
    listFn: filters => firmProfilesService.list(filters),
    listPath: ROUTES.profiles.firmProfiles,
  },
  {
    type: "firm-tabs",
    label: "Firm Tabs",
    listFn: filters => firmTabsService.list(filters),
    listPath: ROUTES.profiles.firmTabs,
  },
  {
    type: "articles",
    label: "Articles",
    listFn: filters => articlesService.list(filters),
    listPath: ROUTES.profiles.articles,
  },
  {
    type: "team-pages",
    label: "Team Pages",
    listFn: filters => teamPagesService.list(filters),
    listPath: ROUTES.profiles.teamPages,
  },
];

/**
 * What the page header should say about this company id (SOFTSUPP-530: the
 * raw uuid title was the Jira symptom). Address-book dereferences merged-away
 * ids to the merge SURVIVOR, so a 200 whose id differs from the requested one
 * means THIS id is dead — the survivor's name must not be presented as this
 * firm's own, because the page lists content still attached to the dead uuid.
 * 404s resolve to "unknown" and any other failure to "failed": the header
 * lookup must never block or break the page.
 */
type CompanyResolution =
  | { kind: "live"; name: string }
  | { kind: "merged"; survivorId: string; survivorName: string }
  | { kind: "unknown" }
  | { kind: "failed" };

function isNotFound(error: unknown): boolean {
  return error instanceof Error && "status" in error && error.status === 404;
}

async function resolveCompany(id: string): Promise<CompanyResolution> {
  try {
    const { data } = await companies.getById(id);
    return data.id === id
      ? { kind: "live", name: data.name }
      : { kind: "merged", survivorId: data.id, survivorName: data.name };
  } catch (error) {
    return isNotFound(error) ? { kind: "unknown" } : { kind: "failed" };
  }
}

function asNonEmpty(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;
  return trimmed;
}

function formatCreated(iso: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * The "Actions" link for a row. Points at the real per-resource staff edit
 * route for the row's content `type`.
 *
 * Firm tabs are the awkward case: `firmTabDetail` needs the tab `kind`, which
 * the per-kind list endpoints don't include as a first-class field — but
 * `firmTabsService.list` tags every row with `tabType`, so we use that. If a
 * firm-tab row somehow arrives without a `tabType`, we can't build a per-kind
 * detail link, so we fall back to the firm-tabs list pre-filtered to this
 * company ("View") rather than emitting a dead link.
 */
function buildEditAction(
  type: ContentType,
  row: ContentRow,
  companyId: string,
): { to: string; label: string } {
  switch (type) {
    case "lawyer-profiles":
      return { to: ROUTES.profiles.lawyerProfileDetail(row.id), label: "Edit" };
    case "firm-profiles":
      return { to: ROUTES.profiles.firmProfileDetail(row.id), label: "Edit" };
    case "articles":
      return { to: ROUTES.profiles.articleDetail(row.id), label: "Edit" };
    case "team-pages":
      return { to: ROUTES.profiles.teamPageDetail(row.id), label: "Edit" };
    case "firm-tabs":
      if (row.tabType) {
        return { to: ROUTES.profiles.firmTabDetail(row.tabType, row.id), label: "Edit" };
      }
      // No kind on the row — fall back to the firm-tabs list filtered to this
      // firm so there's still a working link rather than a broken edit route.
      return {
        to: `${ROUTES.profiles.firmTabs}?companyId=${encodeURIComponent(companyId)}`,
        label: "View",
      };
  }
}

/**
 * Columns for the shared <DataTable>. Built per-section because the "Actions"
 * edit link route depends on the section's content `type`. Columns that
 * don't map to a single field (Actions) reuse a valid `keyof ContentRow`
 * accessor purely to satisfy the type and supply a `render`.
 */
function buildColumns(
  styles: Record<string, string>,
  type: ContentType,
  companyId: string,
): Column<ContentRow>[] {
  return [
    {
      header: "Title / Name",
      accessor: "title",
      render: (_value, row) => <span className={styles.titleCell}>{row.title}</span>,
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => <StatusBadge status={row.status} />,
    },
    {
      header: "Created",
      accessor: "createdAt",
      render: (_value, row) => (
        <span className={styles.dateCell}>{formatCreated(row.createdAt)}</span>
      ),
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value, row) => {
        const action = buildEditAction(type, row, companyId);
        return (
          <Link
            to={action.to}
            className={styles.reviewLink}
            aria-label={`${action.label} ${row.title}`}
          >
            {action.label}
          </Link>
        );
      },
    },
  ];
}

function extractTitle(item: BaseContentItem): string {
  const i = item as Record<string, unknown>;
  const first = asNonEmpty(i.firstName);
  const last = asNonEmpty(i.lastName);
  const fullName = first || last ? [first, last].filter(Boolean).join(" ") : null;
  return (
    fullName ??
    asNonEmpty(i.name) ??
    asNonEmpty(i.title) ??
    asNonEmpty(i.teamName) ??
    asNonEmpty(i.heading) ??
    item.id
  );
}

export default function FirmDetail() {
  const { companyId = "" } = useParams<{ companyId: string }>();
  const { hasPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const [sections, setSections] = useState<SectionData[]>(
    SECTIONS.map(s => ({ type: s.type, label: s.label, rows: [], loading: true, error: null })),
  );
  const [isMoveModalOpen, setMoveModalOpen] = useState(false);
  /** Set by the move modal on a successful apply; closing then refetches every section. */
  const [moveApplied, setMoveApplied] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  /** null until the address-book lookup lands — the uuid title renders until then (no flicker). */
  const [resolved, setResolved] = useState<{
    companyId: string;
    resolution: CompanyResolution;
  } | null>(null);
  const resolution = resolved?.companyId === companyId ? resolved.resolution : null;
  const [lookupAttempt, setLookupAttempt] = useState(0);
  const [lookupPending, setLookupPending] = useState(false);

  // Cosmetic header resolution: upgrades the uuid title to the firm's name
  // (or a merged-into banner for dead ids). Never gates anything — the page
  // renders fully while the lookup is in flight and stays on the uuid if it
  // fails.
  useEffect(() => {
    if (!hasPermission("profiles.read") || !companyId) return;
    let active = true;
    setLookupPending(true);
    void resolveCompany(companyId).then(result => {
      if (active) {
        setResolved({ companyId, resolution: result });
        setLookupPending(false);
      }
    });
    return () => {
      active = false;
    };
  }, [hasPermission, companyId, lookupAttempt]);

  useEffect(() => {
    if (!hasPermission("profiles.read") || !companyId) return;
    // Navigating via the merged-into banner swaps the companyId param on this
    // same mounted component — a slow response for the OLD firm must not
    // overwrite the NEW firm's sections.
    let active = true;

    // Back to skeletons on refetch (reloadKey bump after a content move) —
    // stale pre-move rows must not linger while the reload is in flight.
    setSections(
      SECTIONS.map(s => ({ type: s.type, label: s.label, rows: [], loading: true, error: null })),
    );

    SECTIONS.forEach((section, index) => {
      void (async () => {
        try {
          const result = await section.listFn({ companyId, limit: 50, offset: 0 });
          const rows: ContentRow[] = result.data.map(item => ({
            id: item.id,
            title: extractTitle(item),
            status: item.status,
            createdAt: item.createdAt,
            tabType: item.tabType,
          }));
          if (!active) return;
          setSections(prev =>
            prev.map((s, i) => (i === index ? { ...s, rows, loading: false } : s)),
          );
        } catch (err) {
          if (!active) return;
          const message = err instanceof Error ? err.message : "Unknown error";
          setSections(prev =>
            prev.map((s, i) => (i === index ? { ...s, loading: false, error: message } : s)),
          );
        }
      })();
    });
    return () => {
      active = false;
    };
  }, [hasPermission, companyId, reloadKey]);

  function handleMoveModalClose() {
    setMoveModalOpen(false);
    if (moveApplied) {
      setMoveApplied(false);
      // The move re-pointed rows away from this company — refetch every
      // section so the page shows the post-move (usually empty) state.
      setReloadKey(key => key + 1);
    }
  }

  if (!hasPermission("profiles.read")) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.accessDenied} role="alert">
          <p>You do not have permission to view firm profiles.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={resolution?.kind === "live" ? `Firm: ${resolution.name}` : `Firm: ${companyId}`}
        subtitle="All content profiles associated with this firm"
        meta={
          resolution?.kind === "live" ? <code className={styles.uuid}>{companyId}</code> : undefined
        }
      >
        {/* Deliberately NOT gated on profiles.write (held by portal firm roles and staff QA/CS) — the move tool requires the dedicated staff.profiles_admin-only profiles.bulk_move permission. */}
        {hasPermission("profiles.bulk_move") && (
          <Button variant="outlined" color="default" onClick={() => setMoveModalOpen(true)}>
            Move all content to firm…
          </Button>
        )}
      </PageHeader>

      {resolution?.kind === "merged" && (
        <div className={styles.mergedBanner} role="alert">
          <strong>
            This company was merged into{" "}
            <Link
              to={ROUTES.profiles.firmDetail(resolution.survivorId)}
              className={styles.mergedLink}
            >
              {resolution.survivorName}
            </Link>
            .
          </strong>
          <p className={styles.mergedHint}>
            The content below still points at the merged-away id — the "Move all content to firm…"
            tool re-points it to the survivor.
          </p>
        </div>
      )}

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/profiles/firms" className={styles.backLink}>
          &larr; Back to Firm Inspector
        </Link>
      </nav>

      {hasFlag("firm-logos-editor") && resolution?.kind === "live" && (
        <FirmLogosSection
          companyId={companyId}
          firmName={resolution.name}
          canEdit={hasPermission("profiles.write")}
        />
      )}

      {hasFlag("firm-logos-editor") && resolution?.kind === "failed" && (
        <section className={styles.section} aria-labelledby="section-firm-logos">
          <div className={styles.sectionHeader}>
            <h2 id="section-firm-logos" className={styles.sectionHeading}>
              Logos
            </h2>
          </div>

          <p className={styles.errorText} role="alert">
            The logos couldn't be loaded. Please try again.
          </p>

          <Button
            variant="outlined"
            color="default"
            disabled={lookupPending}
            onClick={() => setLookupAttempt(attempt => attempt + 1)}
          >
            {lookupPending ? "Trying again…" : "Try again"}
          </Button>
        </section>
      )}

      {SECTIONS.map((sectionDef, index) => {
        const section = sections[index];
        if (!section) return null;

        return (
          <section
            key={sectionDef.type}
            className={styles.section}
            aria-labelledby={`section-${sectionDef.type}`}
          >
            <div className={styles.sectionHeader}>
              <h2 id={`section-${sectionDef.type}`} className={styles.sectionHeading}>
                {sectionDef.label}
              </h2>
              {!section.loading && !section.error && (
                <span className={styles.sectionCount} aria-label={`${section.rows.length} items`}>
                  {section.rows.length}
                </span>
              )}
              <Link
                to={`${sectionDef.listPath}?companyId=${encodeURIComponent(companyId)}`}
                className={styles.viewAllLink}
                aria-label={`View all ${sectionDef.label.toLowerCase()} in cross-firm list (filtered to this firm)`}
              >
                View all in cross-firm list
              </Link>
            </div>

            {section.loading && (
              <p className={styles.loadingText} aria-live="polite">
                Loading...
              </p>
            )}

            {!section.loading && section.error && (
              <p className={styles.errorText} role="alert">
                Failed to load {sectionDef.label.toLowerCase()}.
              </p>
            )}

            {!section.loading && !section.error && section.rows.length === 0 && (
              <p className={styles.emptyText}>No {sectionDef.label.toLowerCase()} for this firm.</p>
            )}

            {!section.loading && !section.error && section.rows.length > 0 && (
              <DataTable
                data={section.rows}
                columns={buildColumns(styles, sectionDef.type, companyId)}
                rowKey="id"
              />
            )}
          </section>
        );
      })}

      {/* Deliberately NOT gated on profiles.write (held by portal firm roles and staff QA/CS) — the move tool requires the dedicated staff.profiles_admin-only profiles.bulk_move permission. */}
      {hasPermission("profiles.bulk_move") && (
        <MoveCompanyContentModal
          sourceCompanyId={companyId}
          isOpen={isMoveModalOpen}
          onClose={handleMoveModalClose}
          onApplied={() => setMoveApplied(true)}
        />
      )}
    </div>
  );
}
